import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Alert, Box, Button, Typography, useTheme } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface TransactionLimitAlertProps {
    onNavigateToPlans: () => void;
    transactionCount: number;
    planLimit: number;
    taxableAssets: string[];
    issuesTransactionCount: number; // This now represents taxableTransactionCount
}

const TransactionLimitAlert: React.FC<TransactionLimitAlertProps> = ({
    onNavigateToPlans,
    transactionCount,
    planLimit,
    taxableAssets,
    issuesTransactionCount,
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';

    const countOverLimit = Math.max(0, Math.abs(transactionCount - planLimit));
    const hasNoTransactions = transactionCount === 0;

    // Dark mode uses a darker background with bright text
    // Light mode uses a light blue background with dark blue text
    const styles = {
        backgroundColor: isDarkMode ? '#1e3a5f' : '#e3f2fd',
        textPrimary: isDarkMode ? '#ffffff' : '#1565c0',
        textSecondary: isDarkMode ? '#bbdefb' : '#1976d2',
        border: isDarkMode ? '#2196f3' : '#bbdefb',
        buttonBg: '#2196f3',
        buttonHover: '#1976d2',
        iconColor: isDarkMode ? '#90caf9' : '#2196f3',
    };

    return (
        <Box sx={{ mb: 3 }}>
            <Alert
                severity="info"
                icon={<InfoOutlinedIcon sx={{ fontSize: '1.5rem', color: styles.iconColor }} />}
                sx={{
                    backgroundColor: styles.backgroundColor,
                    color: styles.textPrimary,
                    fontWeight: 'bold',
                    fontSize: '1.1rem',
                    border: `1px solid ${styles.border}`,
                    borderRadius: 1.5,
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: hasNoTransactions ? 'flex-start' : 'space-between',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                }}
                action={
                    !hasNoTransactions ? (
                        <Button
                            variant="contained"
                            onClick={onNavigateToPlans}
                            sx={{
                                backgroundColor: styles.buttonBg,
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '1.1rem',
                                textTransform: 'none',
                                px: 2,
                                py: 1,
                                '&:hover': {
                                    backgroundColor: styles.buttonHover,
                                },
                            }}
                        >
                            {t('taxReports.buttons.unlockNowAndReceiveReport')}
                        </Button>
                    ) : null
                }
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        alignItems: 'flex-start',
                    }}
                >
                    {hasNoTransactions ? (
                        <Typography
                            variant="body1"
                            sx={{
                                fontWeight: 'bold',
                                fontSize: '1.1rem',
                                color: styles.textPrimary,
                            }}
                        >
                            {t('taxReports.noTransactionsDetected')}
                        </Typography>
                    ) : (
                        <>
                            <Box
                                component="ul"
                                sx={{
                                    listStyle: 'disc',
                                    paddingLeft: '20px',
                                    margin: 0,
                                    width: '100%',
                                }}
                            >
                                <Box component="li" sx={{ mb: 0.5 }}>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            fontWeight: 'bold',
                                            fontSize: '1rem',
                                            color: styles.textPrimary,
                                        }}
                                    >
                                        {t('taxReports.taxableTransactionsDetected', { count: issuesTransactionCount })}
                                    </Typography>
                                </Box>
                                {taxableAssets.length > 0 && (
                                    <Box component="li" sx={{ mb: 0.5 }}>
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                fontWeight: 'bold',
                                                fontSize: '1rem',
                                                color: styles.textPrimary,
                                            }}
                                        >
                                            {t('taxReports.taxableAssets', { assets: taxableAssets.join(', ') })}
                                        </Typography>
                                    </Box>
                                )}
                                <Box component="li">
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            fontWeight: 'bold',
                                            fontSize: '1rem',
                                            color: styles.textPrimary,
                                        }}
                                    >
                                        {t('taxReports.transactionLimitExceeded', { count: countOverLimit })}
                                    </Typography>
                                </Box>
                            </Box>
                        </>
                    )}
                </Box>
            </Alert>
        </Box>
    );
};

export default TransactionLimitAlert;
